import React, { useEffect, useMemo, useRef, useState } from 'react'
import parse, { attributesToProps, domToReact } from 'html-react-parser'
import ScriptTag from '@v4/components/common/ScriptTag'
import sanitizeHtml from 'sanitize-html'
import AppStore from '@v4/store/appStore'
import PageBuilderSelector from '@v4/utils/selectors/PageBuilderSelector'
import PageData from '@v4/utils/data/PageData'
import useReplacedText from '@v4/hooks/useReplacedText'

const ALLOWED_TAGS = sanitizeHtml.defaults.allowedTags.concat([
	'img',
	'script',
	'link',
	'style',
	'button',
	'iframe',
	'input',
	'form',
	'canvas',
	'stripe-buy-button',
])
const ALLOWED_SCHEMES = sanitizeHtml.defaults.allowedSchemes.concat(['javascript'])

const useParsedHtml = (html, allowedTags = ALLOWED_TAGS) => {
	const blocked = AppStore.useStoreState(state => state.result.blocked)
	const signUpRequired = AppStore.useStoreState(state => state.result.data.sing_up_required)
	const isBuilderMode = AppStore.useStoreState(state => state.general.isBuilderMode)
	const pageType = PageBuilderSelector.useType()
	const [hidden, setHidden] = useState(!!signUpRequired && PageData.isTypeResult(pageType))
	const hiddenHtml = !isBuilderMode && (blocked || hidden)
	const hiddenHtmlRef = useRef(hiddenHtml)
	const parsedHtml = useReplacedText(html)

	useEffect(() => {
		if (!hidden) {
			return undefined
		}
		const timeout = setTimeout(() => {
			setHidden(false)
		}, 300)

		return () => {
			clearTimeout(timeout)
		}
	}, [hidden])

	return useMemo(() => {
		if (!parsedHtml || hiddenHtml) {
			return ''
		}

		const options = {
			trim: true,
			replace: domNode => {
				if (domNode.type === 'script') {
					const attribs = attributesToProps(domNode.attribs)
					return (
						<ScriptTag
							onLoad={() => {
								if (isBuilderMode || hiddenHtml !== hiddenHtmlRef.current) {
									window.dispatchEvent(new Event('load'))
								}
							}}
							{...attribs}
						>
							{domToReact(domNode.children, options)}
						</ScriptTag>
					)
				}

				if (domNode.type === 'tag' && domNode.name === 'input') {
					const { value, ...attribs } = attributesToProps(domNode.attribs)
					return <input {...attribs} defaultValue={value || ''} />
				}

				return domNode
			},
		}

		const sanitizedHtml = sanitizeHtml(parsedHtml, {
			allowedTags,
			allowedSchemes: ALLOWED_SCHEMES,
			allowedAttributes: false,
			allowVulnerableTags: true,
		})

		return parse(sanitizedHtml, options)
	}, [parsedHtml, hiddenHtml, allowedTags, isBuilderMode])
}

export default useParsedHtml
